import { CATALOGUE_POLICES, policesDeLaCharte } from '../../contract/index.js';
/** `@font-face` des polices de la charte, auto-hébergées (D12). */
function fontFaces(charte) {
    return policesDeLaCharte(charte)
        .flatMap((nom) => {
        const definition = CATALOGUE_POLICES[nom];
        return definition.fichiers.map((fichier) => `@font-face {
  font-family: "${definition.famille}";
  src: url("${fichier.chemin}") format("woff2");
  font-weight: ${String(fichier.graisse)};
  font-style: ${fichier.style};
  font-display: swap;
}`);
    })
        .join('\n');
}
function pile(nom) {
    const definition = CATALOGUE_POLICES[nom];
    return `"${definition.famille}", ${definition.repli}`;
}
/**
 * Feuille de style du thème **Cartel**, d'après `designs/b-cartel.html` : le
 * cartel de musée — typographie éditoriale, texte d'abord, grandes marges,
 * images sobres, navigation textuelle, filets doubles à l'imprimé. Même
 * balisage que Cimaise (`Corps`, `composants`, `pages` sont identiques) : ce
 * qui fait le thème, ici, tient tout entier dans cette feuille.
 */
export function cssCartel(charte) {
    return `${fontFaces(charte)}

.achat { margin-block: 24px; }
.bouton-achat { display: inline-flex; align-items: center; min-height: 44px; padding: 12px 20px; border: 1px solid var(--ink); color: var(--ink); text-decoration: none; }
.bouton-achat:focus-visible { outline: 2px solid var(--ink); outline-offset: 4px; }

:root {
  --couleur-principale: ${charte.couleurPrincipale};
  --couleur-secondaire: ${charte.couleurSecondaire};
  --ground: #F1F0EA;
  --ink: var(--couleur-principale);
  --accent: var(--couleur-secondaire);
  --muted: color-mix(in srgb, var(--ink) 62%, var(--ground));
  --faint: color-mix(in srgb, var(--ink) 42%, var(--ground));
  --hair: color-mix(in srgb, var(--ink) 16%, var(--ground));
  --rule: var(--ink);
  --plate: color-mix(in srgb, var(--ink) 12%, #C9C6BB);
  --titres: ${pile(charte.policeTitres)};
  --texte: ${pile(charte.policeTexte)};
  --marge: 56px;
  --blocs: ${String(charte.accueil.length)};
}

@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]) {
    --ground: #17181A;
    --ink: color-mix(in srgb, var(--couleur-principale) 14%, #E9E8E3);
    --muted: color-mix(in srgb, var(--ink) 64%, var(--ground));
    --faint: color-mix(in srgb, var(--ink) 42%, var(--ground));
    --hair: color-mix(in srgb, var(--ink) 16%, var(--ground));
    --plate: color-mix(in srgb, var(--ink) 14%, #2C2A26);
  }
}

*, *::before, *::after { box-sizing: border-box; }

html { background: var(--ground); }

body {
  margin: 0;
  background: var(--ground);
  color: var(--ink);
  font-family: var(--texte);
  font-size: 17px;
  line-height: 1.65;
  -webkit-font-smoothing: antialiased;
}

a { color: inherit; text-decoration: none; }
a:hover { color: var(--accent); }
a:focus-visible, input:focus-visible, textarea:focus-visible, button:focus-visible {
  outline: 1.5px solid var(--accent);
  outline-offset: 3px;
}

img { max-width: 100%; height: auto; display: block; }
picture { display: contents; }

h1, h2, h3, h4 { font-family: var(--titres); font-weight: 400; letter-spacing: -0.008em; }

em { font-style: italic; }

.saut {
  position: absolute;
  left: -9999px;
  top: 0;
  background: var(--ground);
  color: var(--ink);
  padding: 12px 18px;
  z-index: 10;
}
.saut:focus { left: 0; }

.invisible {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* Champ piège des formulaires publics : hors de l’écran, jamais rempli à la main. */
.piege {
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.wrap { padding: 0 var(--marge); max-width: 1220px; margin: 0 auto; }

.label {
  font-family: var(--texte);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent);
  margin: 0;
}

.lien-souligne { border-bottom: 1px solid currentColor; }

.sous-label { margin-top: 52px; padding-bottom: 16px; }

.entete {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
  padding-top: 30px;
  padding-bottom: 20px;
  margin-bottom: 44px;
  border-bottom: 3px double var(--rule);
  flex-wrap: wrap;
}
.logo { font-family: var(--titres); font-size: clamp(26px, 4vw, 38px); font-weight: 400; letter-spacing: -0.01em; }
.logo img { display: inline-block; }
.entete nav { display: flex; gap: 22px; font-size: 12.5px; color: var(--muted); flex-wrap: wrap; padding-bottom: 8px; }
.entete nav a[aria-current="page"] { color: var(--ink); border-bottom: 1px solid var(--accent); }
.entete .lang { color: var(--faint); letter-spacing: 0.1em; text-transform: uppercase; }

section { padding-bottom: 56px; margin-bottom: 56px; border-bottom: 1px solid var(--hair); }
section:last-of-type { border-bottom: 0; margin-bottom: 0; }

.tete-section {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-bottom: 22px;
  margin-bottom: 22px;
  border-bottom: 1px solid var(--rule);
  gap: 24px;
}

.plate { background: var(--plate); width: 100%; }
.une .visuel img,
.oeuvre .cadre img,
.fiche-oeuvre .visuels img,
.mosaique img,
.portrait img,
.vignette img { background: var(--plate); }

figure { margin: 0; }
figcaption { font-size: 12px; color: var(--muted); margin-top: 8px; }

/* Article de tête : marge éditoriale à gauche, texte en colonnes à droite */
.une { display: grid; grid-template-columns: 190px 1fr; gap: 0 44px; }
.une .visuel { aspect-ratio: 3 / 2; overflow: hidden; margin-bottom: 12px; }
.une .visuel img { width: 100%; height: 100%; object-fit: cover; }
.une .cartel { grid-column: 2; display: block; margin-top: 0; max-width: none; }
.une h1 { font-size: clamp(28px, 4vw, 44px); line-height: 1.08; margin: 8px 0 4px; text-wrap: balance; }
.une .dates { font-size: 13px; color: var(--muted); margin: 0 0 22px; }
.une p.txt { font-size: 16px; max-width: 62ch; margin: 0; columns: 2; column-gap: 36px; }
.une .more { display: inline-block; margin-top: 18px; font-size: 12.5px; text-transform: uppercase; letter-spacing: 0.08em; }
@media (max-width: 860px) {
  .une { grid-template-columns: 1fr; }
  .une .cartel { grid-column: 1; }
  .une p.txt { columns: 1; }
}

/* Œuvres, comme une liste de prix de catalogue */
.grille { display: block; list-style: none; padding: 0; margin: 0; border-top: 1px solid var(--rule); }
.oeuvre { display: grid; grid-template-columns: 96px 1fr; gap: 18px 26px; padding: 18px 0; border-bottom: 1px solid var(--hair); align-items: start; }
.oeuvre a { display: contents; }
.oeuvre .cadre { display: block; aspect-ratio: 4 / 5; height: auto; }
.oeuvre .cadre img { width: 100%; height: 100%; object-fit: cover; }
.oeuvre dl { margin: 0; font-size: 15.5px; line-height: 1.5; }
.oeuvre dt { font-weight: 500; display: inline; }
.oeuvre dt::after { content: ", "; font-weight: 400; }
.oeuvre dd { margin: 0; display: inline; color: inherit; }
.oeuvre dd::before { content: ", "; }
.oeuvre dd:first-of-type::before { content: none; }
.oeuvre dd.prix { display: block; margin-top: 6px; font-size: 13px; color: var(--muted); }
.oeuvre dd.prix::before { content: none; }
.oeuvre dd.enq { display: block; margin-top: 2px; font-size: 11.5px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--accent); }
.oeuvre dd.enq::before { content: none; }
@media (max-width: 640px) { .oeuvre { grid-template-columns: 64px 1fr; } }

/* Listes typographiques */
.colonnes { columns: 3; column-gap: 40px; font-size: 17px; line-height: 2; list-style: none; padding: 0; margin: 0; border-top: 1px solid var(--rule); padding-top: 12px; }
.colonnes li { break-inside: avoid; }
.colonnes a { display: block; }

.rangs { list-style: none; padding: 0; margin: 0; border-top: 1px solid var(--rule); }
.rang {
  display: grid;
  grid-template-columns: 190px 1fr 200px;
  gap: 16px 28px;
  padding: 16px 0;
  border-bottom: 1px solid var(--hair);
  align-items: baseline;
  font-size: 13.5px;
}
.rang .d { font-size: 11.5px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted); }
.rang .t { font-size: 18px; font-family: var(--titres); }
.rang .t span { display: block; font-size: 13px; font-family: var(--texte); color: var(--muted); margin-top: 3px; }
.rang .p { font-size: 12.5px; color: var(--muted); }

/* Pages de contenu */
.page-tete { padding-bottom: 40px; }
.page-tete h1 { font-size: 40px; margin: 10px 0 0; max-width: 22ch; text-wrap: balance; }
.page-tete .chapeau { font-size: 15.5px; color: var(--muted); margin: 16px 0 0; max-width: 56ch; }

.prose { max-width: 64ch; font-size: 16px; }
.prose p { margin: 0 0 1.1em; }

.deux-colonnes { display: grid; grid-template-columns: 300px 1fr; gap: 52px; align-items: start; }
.portrait img { width: 100%; aspect-ratio: 4 / 5; object-fit: cover; }

.fiche-oeuvre { display: grid; grid-template-columns: 340px 1fr; gap: 52px; align-items: start; }
.fiche-oeuvre .visuels { display: grid; gap: 20px; }
.fiche-oeuvre .visuels img { width: 100%; }
.fiche-oeuvre .cartel h1 { font-size: 30px; margin: 8px 0 0; }
.fiche-oeuvre .cartel .artiste { font-size: 15px; margin: 0; }
.fiche-oeuvre .cartel dl { margin: 22px 0 0; font-size: 14px; }
.fiche-oeuvre .cartel dt { font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--muted); margin-top: 14px; }
.fiche-oeuvre .cartel dd { margin: 3px 0 0; }
.fiche-oeuvre .cartel .prose { margin-top: 26px; }
.fiche-oeuvre .cartel .formulaire { margin-top: 36px; }

.mosaique { display: grid; grid-template-columns: repeat(2, 1fr); gap: 28px; list-style: none; padding: 0; margin: 0; }
.mosaique img { width: 100%; }

/* Formulaires */
.formulaire { max-width: 520px; }
.champ { display: block; margin-bottom: 22px; }
.champ span { display: block; font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--muted); margin-bottom: 6px; }
.champ input, .champ textarea {
  width: 100%;
  border: 0;
  border-bottom: 1px solid var(--ink);
  background: transparent;
  color: var(--ink);
  font: inherit;
  font-size: 15px;
  padding: 6px 0;
}
.champ textarea { min-height: 120px; resize: vertical; }
.formulaire button {
  border: 0;
  background: transparent;
  color: var(--accent);
  font: inherit;
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border-bottom: 1.5px solid var(--accent);
  padding: 0 0 6px;
  cursor: pointer;
}
.formulaire button:hover { color: var(--ink); border-color: var(--ink); }
.formulaire [data-etat] { display: block; margin-top: 14px; font-size: 13px; color: var(--muted); }
.formulaire [data-etat][data-erreur="oui"] { color: var(--accent); }

.newsletter { max-width: 520px; }
.newsletter p { font-size: 14px; color: var(--muted); margin: 10px 0 20px; }
.newsletter .ligne { display: flex; gap: 12px; border-bottom: 1.5px solid var(--rule); padding-bottom: 8px; }
.newsletter .ligne input { flex: 1; border: 0; background: transparent; color: var(--ink); font: inherit; font-size: 15px; padding: 0; }
.newsletter .ligne button { border: 0; background: transparent; color: var(--accent); font: inherit; font-weight: 600; font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer; padding: 0; }
.newsletter .ligne button:hover { color: var(--ink); }

/* Plan du site */
.plan { display: grid; grid-template-columns: repeat(3, 1fr); gap: 44px 40px; }
.plan ul { list-style: none; padding: 0; margin: 12px 0 0; font-size: 14px; line-height: 1.9; }

/* Pied de page */
.pied {
  border-top: 3px double var(--rule);
  padding-top: 30px;
  padding-bottom: 60px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px 40px;
  font-size: 13px;
  color: var(--muted);
}
.pied b, .pied h2 {
  display: block;
  color: var(--ink);
  font-family: var(--texte);
  font-weight: 600;
  font-size: 10.5px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin: 0 0 10px;
}
.pied ul { list-style: none; padding: 0; margin: 0; }
.pied address { font-style: normal; }
.pied address + address { margin-top: 10px; }
.mention { grid-column: 1 / -1; border-top: 1px solid var(--hair); padding-top: 18px; font-size: 11.5px; color: var(--faint); display: flex; flex-wrap: wrap; gap: 8px 26px; }

@media (max-width: 1000px) {
  .deux-colonnes, .fiche-oeuvre { grid-template-columns: 1fr; gap: 32px; }
  .plan { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 760px) {
  :root { --marge: 20px; }
  body { font-size: 16px; }
  .une { grid-template-columns: 1fr; }
  .une .cartel { grid-column: 1; }
  .oeuvre { grid-template-columns: 72px 1fr; }
  .rang { grid-template-columns: 1fr; gap: 4px; }
  .pied { grid-template-columns: 1fr 1fr; }
  .colonnes { columns: 2; }
  section { padding-bottom: 40px; margin-bottom: 40px; }
  .entete { padding-bottom: 16px; margin-bottom: 32px; }
}

@media (max-width: 520px) {
  .mosaique, .plan { grid-template-columns: 1fr; }
  .colonnes { columns: 1; }
}

@media print {
  .entete nav, .saut, .newsletter, .formulaire { display: none; }
}
`;
}
//# sourceMappingURL=css.js.map